import React, { useEffect, useMemo, useState } from "react";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

const ORDER_TYPES = [
  { value: "J", label: "New Joining" },
  { value: "T", label: "Transfer" },
  { value: "P", label: "Promotion" },
  { value: "S", label: "Promotion & Transfer" },
  { value: "E", label: "Extended Service" },
  { value: "R", label: "Relieving/Retirement" },
];

const STATUSES = [
  { value: "P", label: "Initiated" },
  { value: "R", label: "Relieved" },
  { value: "J", label: "Joined" },
  { value: "S", label: "Stand Relieved" },
  { value: "C", label: "Cancelled" },
];

function formatDate(val) {
  if (!val || val === "0000-00-00") return "";
  const d = new Date(val);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("en-GB");
}

export default function ShiftEmployee() {
  const localUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
  }, []);

  const moduleId = parseInt(localUser?.moduleId, 10);
  const empId    = parseInt(localUser?.id,        10);
  const deptId   = parseInt(localUser?.deptid,   10);

  const [empOptions,   setEmpOptions]   = useState([]);
  const [designations, setDesignations] = useState([]);
  const [departments,  setDepartments]  = useState([]);
  const [history,      setHistory]      = useState([]);

  const [employeeID,       setEmployeeID]       = useState("");
  const [orderType,        setOrderType]        = useState("J");
  const [statusVal,        setStatusVal]        = useState("P");
  const [orderDate,        setOrderDate]        = useState("");
  const [orderNumber,      setOrderNumber]      = useState("");
  const [designationID,    setDesignationID]    = useState("");
  const [departmentID,     setDepartmentID]     = useState("");
  const [bDepartmentID,    setBDepartmentID]    = useState("");
  const [fromDate,         setFromDate]         = useState("");
  const [toDate,           setToDate]           = useState("");
  const [confirmationDate, setConfirmationDate] = useState("");
  const [joiningTime,      setJoiningTime]      = useState("");
  const [remarks,          setRemarks]          = useState("");

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const fetchHistory = () => {
    fetch(`${API_ROOT}/api/employees/shift/history?moduleId=${moduleId}&empId=${empId}`)
      .then(r => r.json())
      .then(data => setHistory(Array.isArray(data) ? data : []))
      .catch(() => {});
  };

  useEffect(() => {
    if (moduleId !== 4) return;

    fetch(`${API_ROOT}/api/employees/shift/employee-options?moduleId=${moduleId}&empId=${empId}&deptId=${deptId}`)
      .then(r => r.json())
      .then(data => setEmpOptions(Array.isArray(data) ? data : []))
      .catch(() => {});

    fetch(`${API_ROOT}/api/employees/shift/designations`)
      .then(r => r.json())
      .then(data => setDesignations(Array.isArray(data) ? data : []))
      .catch(() => {});

    fetch(`${API_ROOT}/api/directory/departments`)
      .then(r => r.json())
      .then(data => setDepartments(Array.isArray(data) ? data : []))
      .catch(() => {});

    fetchHistory();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (moduleId !== 4) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">Unauthorized access Or Session Timed out.</div>
      </div>
    );
  }

  const handleSaveRecord = async (e) => {
    e.preventDefault();
    if (!employeeID) { setMessage({ text: "Please select an employee.", type: "danger" }); return; }
    setLoading(true);
    setMessage({ text: "", type: "" });
    try {
      const res  = await fetch(`${API_ROOT}/api/employees/shift/save`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          employeeID, designationID, departmentID, BDepartmentID: bDepartmentID,
          fromDate, toDate, confirmationDate, joiningTime,
          orderDate, orderNumber, orderType, status: statusVal, remarks,
          loginID: empId, moduleId,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage({ text: data.message || "Saved.", type: "success" });
        setOrderType("J"); setStatusVal("P"); setOrderDate(""); setOrderNumber("");
        setDesignationID(""); setDepartmentID(""); setBDepartmentID("");
        setFromDate(""); setToDate(""); setConfirmationDate("");
        setJoiningTime(""); setRemarks("");
        fetchHistory();
      } else {
        setMessage({ text: data.message || "Error saving.", type: "danger" });
      }
    } catch {
      setMessage({ text: "Error saving record.", type: "danger" });
    } finally {
      setLoading(false);
    }
  };

  const deptSelect = (value, onChange, label) => (
    <select className="form-select form-select-sm" value={value} onChange={e => onChange(e.target.value)}>
      <option value="">{label}</option>
      {departments.map(d => (
        <option key={d.DepartmentID} value={d.DepartmentID}>{d.Department}</option>
      ))}
    </select>
  );

  return (
    <div className="container-fluid mt-3">
      <h4 className="ctitle">Transfer/Promotions/Assign Set</h4>

      {message.text && (
        <div className={`alert alert-${message.type} alert-dismissible`}>
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage({ text: "", type: "" })} />
        </div>
      )}

      {/* Service History Entry */}
      <div className="card mb-3">
        <div className="card-header bg-light fw-semibold">Record Transfer / Promotion / Service Event</div>
        <div className="card-body">
          <form className="row g-2" onSubmit={handleSaveRecord}>
            <div className="col-md-5">
              <label className="form-label">Select Employee</label>
              <select className="form-select form-select-sm" value={employeeID} onChange={e => setEmployeeID(e.target.value)} required>
                <option value="">-- Select Employee --</option>
                {empOptions.map(emp => (
                  <option key={emp.EmployeeID} value={emp.EmployeeID}>
                    {emp.EmpName} — {emp.Department} — {emp.Designation}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Select Order Type</label>
              <select className="form-select form-select-sm" value={orderType} onChange={e => setOrderType(e.target.value)}>
                {ORDER_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label">Status</label>
              <select className="form-select form-select-sm" value={statusVal} onChange={e => setStatusVal(e.target.value)}>
                {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label">Order Date</label>
              <input className="form-control form-control-sm" type="date" value={orderDate} onChange={e => setOrderDate(e.target.value)} />
            </div>

            <div className="col-md-3">
              <label className="form-label">Order Number</label>
              <input className="form-control form-control-sm" type="text" value={orderNumber} onChange={e => setOrderNumber(e.target.value)} placeholder="Order Number..." />
            </div>

            <div className="col-md-4">
              <label className="form-label">New Designation</label>
              <select className="form-select form-select-sm" value={designationID} onChange={e => setDesignationID(e.target.value)}>
                <option value="">-- Select Designation --</option>
                {designations.map(d => (
                  <option key={d.DesignationID} value={d.DesignationID}>{d.Designation}</option>
                ))}
              </select>
            </div>

            <div className="col-md-5">
              <label className="form-label">New Department</label>
              {deptSelect(departmentID, setDepartmentID, "-- Select Department --")}
            </div>

            <div className="col-md-5">
              <label className="form-label">Salary Charged from Department</label>
              {deptSelect(bDepartmentID, setBDepartmentID, "-- Select Department --")}
            </div>

            <div className="col-md-2">
              <label className="form-label">From Date (Actual)</label>
              <input className="form-control form-control-sm" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
            </div>

            <div className="col-md-2">
              <label className="form-label">To Date (Actual)</label>
              <input className="form-control form-control-sm" type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
            </div>

            <div className="col-md-2">
              <label className="form-label">Confirmation Date</label>
              <input className="form-control form-control-sm" type="date" value={confirmationDate} onChange={e => setConfirmationDate(e.target.value)} />
            </div>

            <div className="col-md-2">
              <label className="form-label">Joining Time</label>
              <select className="form-select form-select-sm" value={joiningTime} onChange={e => setJoiningTime(e.target.value)}>
                <option value="">Select</option>
                <option value="AN">AN</option>
                <option value="FN">FN</option>
                <option value="NA">NA</option>
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label">Remarks</label>
              <input className="form-control form-control-sm" type="text" value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Remarks..." />
            </div>

            <div className="col-12 mt-1">
              <button className="btn btn-success btn-sm" type="submit" disabled={loading}>
                {loading ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Service History Table */}
      <h5>Employee Service History</h5>
      <div className="table-responsive">
        <table className="table table-bordered table-sm align-middle" style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
          <thead style={{ '--bs-table-bg': '#008080', color: 'white' }}>
            <tr>
              <th>SNo</th>
              <th>Name</th>
              <th>Designation</th>
              <th>Department</th>
              <th>Budget Dept</th>
              <th>Order Type</th>
              <th>Status</th>
              <th>From Date</th>
              <th>To Date</th>
              <th>Confirmation Date</th>
              <th>Joining Time</th>
              <th>Order Date</th>
              <th>Order No</th>
              <th>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {history.length === 0 ? (
              <tr>
                <td colSpan={14} className="text-center text-muted py-2">No records found.</td>
              </tr>
            ) : (
              history.map((row, idx) => (
                <tr key={row.ServiceHistoryID ?? idx}>
                  <td>{idx + 1}</td>
                  <td>{row.EmpName}</td>
                  <td>{row.Designation}</td>
                  <td>{row.Department}</td>
                  <td>{row.BDepartment || ""}</td>
                  <td>{row.OrderType}</td>
                  <td>{row.Status}</td>
                  <td>{formatDate(row.FromDate)}</td>
                  <td>{formatDate(row.ToDate)}</td>
                  <td>{formatDate(row.ConfirmationDate)}</td>
                  <td>{row.JoiningTime || ""}</td>
                  <td>{formatDate(row.OrderDate)}</td>
                  <td>{row.OrderNumber}</td>
                  <td>{row.Remarks}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
